/**
 * useContainerSettings - Meta-driven container behavior hook
 * 
 * Provides standardized container positioning, animations, pinning, and CyKL access
 * based on OPERATIONAL_PREFERENCES.containerSystem from styleCard.js
 * 
 * Usage:
 * const { 
 *   containerClasses, 
 *   animationProps, 
 *   isPinned, 
 *   togglePin,
 *   CyKLButton 
 * } = useContainerSettings('contextualOrientation');
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Pin, PinOff } from 'lucide-react';
import { OPERATIONAL_PREFERENCES } from '../utils/styleCard';
import { useCyKL } from '../contexts/CyKLContext';

const { containerSystem, cyklSystem } = OPERATIONAL_PREFERENCES;

/**
 * Hook for standardized container behavior
 * @param {string} containerId - Container identifier
 * @param {object} options - Override options
 */
export const useContainerSettings = (containerId, options = {}) => {
  // Pinning state
  const [isPinned, setIsPinned] = useState(() => {
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem(containerSystem.pinning.storageKey);
    if (stored) {
      const pinned = JSON.parse(stored);
      return pinned[containerId] || false;
    }
    return false;
  });

  const [pinPosition, setPinPosition] = useState('right');

  // Check if container supports pinning
  const isPinnable = containerSystem.pinning.pinnableContainers.includes(containerId);
  
  // Check if container shows CyKL access
  const showCyKL = containerSystem.tutorialAccess.showIn.includes(containerId);

  // Toggle pin state
  const togglePin = useCallback((position = 'right') => {
    if (!isPinnable) return;
    
    setIsPinned(prev => {
      const newState = !prev;
      
      // Save to localStorage
      const stored = localStorage.getItem(containerSystem.pinning.storageKey);
      const pinned = stored ? JSON.parse(stored) : {};
      
      if (newState) {
        pinned[containerId] = { position, timestamp: Date.now() };
      } else {
        delete pinned[containerId];
      }
      
      localStorage.setItem(containerSystem.pinning.storageKey, JSON.stringify(pinned));
      
      // Dispatch event for other components
      window.dispatchEvent(new CustomEvent('tod_container_pin_change', {
        detail: { containerId, isPinned: newState, position }
      }));
      
      return newState;
    });
    
    if (!isPinned) {
      setPinPosition(position);
    }
  }, [containerId, isPinnable, isPinned]);

  // Get container positioning classes based on state
  const containerClasses = useMemo(() => {
    const { positioning, pinning } = containerSystem;
    const baseClasses = [
      'flex',
      `items-${positioning.alignItems}`,
      `justify-${positioning.justifyContent}`
    ];

    if (isPinned && isPinnable) {
      const pos = pinning.positions[pinPosition];
      if (pos.side === 'right') {
        return ['fixed', 'top-0', 'right-0', 'h-full', `w-[${pos.width}]`, 'z-40'];
      } else if (pos.side === 'left') {
        return ['fixed', 'top-0', 'left-0', 'h-full', `w-[${pos.width}]`, 'z-40'];
      }
      // Add more position handlers as needed
    }

    return baseClasses;
  }, [isPinned, isPinnable, pinPosition]);

  // Animation props for framer-motion
  const animationProps = useMemo(() => {
    const { animations, overlayBehavior } = containerSystem;
    const { enter, exit, slideIn } = animations;

    // Different animations based on container type
    const isSlidePanel = ['contextualOrientation', 'glossaryPanel', 'searchHistory'].includes(containerId);

    if (isSlidePanel && !isPinned) {
      return {
        initial: { x: '100%' },
        animate: { x: 0 },
        exit: { x: '100%' },
        transition: {
          type: slideIn.ease,
          stiffness: slideIn.springConfig.stiffness,
          damping: slideIn.springConfig.damping
        }
      };
    }

    // Center-anchored modal animation
    return {
      initial: { 
        opacity: enter.opacity.from, 
        scale: enter.scale.from, 
        y: enter.y.from 
      },
      animate: { 
        opacity: enter.opacity.to, 
        scale: enter.scale.to, 
        y: enter.y.to 
      },
      exit: { 
        opacity: exit.opacity.from, 
        scale: exit.scale.from, 
        y: exit.y.from 
      },
      transition: { 
        duration: enter.duration, 
        ease: enter.ease 
      }
    };
  }, [containerId, isPinned]);

  // Backdrop animation for modals
  const backdropProps = useMemo(() => ({
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    className: 'fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center'
  }), []);

  // CyKL button props
  const cyKLButtonProps = useMemo(() => {
    if (!showCyKL) return null;
    
    const { tutorialAccess } = containerSystem;
    const { identity, animations } = cyklSystem;
    
    return {
      symbol: identity.symbol,
      tooltip: tutorialAccess.tooltip,
      position: tutorialAccess.position,
      className: 'cykl-trigger',
      'data-testid': `cykl-button-${containerId}`,
      'aria-label': `Ask ${identity.name} for help with this panel`
    };
  }, [showCyKL, containerId]);

  // Pin button props
  const pinButtonProps = useMemo(() => {
    if (!isPinnable) return null;
    
    const { pinButton } = containerSystem.pinning;
    
    return {
      icon: isPinned ? pinButton.iconPinned : pinButton.icon,
      tooltip: isPinned ? pinButton.tooltipPinned : pinButton.tooltip,
      onClick: () => togglePin(),
      className: `pin-button ${isPinned ? 'pinned' : ''}`,
      'data-testid': `pin-button-${containerId}`,
      'aria-pressed': isPinned
    };
  }, [isPinnable, isPinned, containerId, togglePin]);

  return {
    // State
    isPinned,
    isPinnable,
    pinPosition,
    showCyKL,
    
    // Actions
    togglePin,
    setPinPosition,
    
    // Style/Animation props
    containerClasses,
    animationProps,
    backdropProps,
    
    // Component props
    cyKLButtonProps,
    pinButtonProps,
    
    // Config references
    containerConfig: containerSystem,
    cyKLConfig: cyklSystem
  };
};

/**
 * Get all currently pinned containers
 */
export const getPinnedContainers = () => {
  if (typeof window === 'undefined') return {};
  const stored = localStorage.getItem(containerSystem.pinning.storageKey);
  return stored ? JSON.parse(stored) : {};
};

/**
 * Clear all pinned containers
 */
export const clearPinnedContainers = () => {
  localStorage.removeItem(containerSystem.pinning.storageKey);
  window.dispatchEvent(new CustomEvent('tod_container_pin_change', {
    detail: { action: 'clear_all' }
  }));
};

/**
 * CyKL Dragon Button Component
 * Renders the dragon symbol for accessing CyKL help
 * Uses global CyKLContext to open chat from anywhere in the app
 */
export const CyKLButton = ({ containerId, onClick, className = '', initialMessage = null, context = {} }) => {
  const { cyKLButtonProps } = useContainerSettings(containerId);
  
  // Always call the hook - it's safe because CyKLProvider wraps the entire app
  const cyKLContext = useCyKL();
  
  if (!cyKLButtonProps) return null;
  
  // Handle click - open CyKL chat with optional initial message
  const handleClick = (e) => {
    e?.stopPropagation?.();
    
    // If custom onClick provided, use it
    if (onClick) {
      onClick(e, initialMessage);
      return;
    }
    
    // Otherwise use global CyKL context to open chat
    if (cyKLContext?.openChat) {
      cyKLContext.openChat({
        initialMessage,
        context: {
          ...context,
          sourcePanel: containerId
        }
      });
    }
  };
  
  return (
    <button
      onClick={handleClick}
      className={`
        text-lg hover:scale-110 transition-transform cursor-pointer
        hover:drop-shadow-[0_0_8px_rgba(255,215,0,0.5)]
        ${className}
      `}
      title={cyKLButtonProps.tooltip}
      data-testid={cyKLButtonProps['data-testid']}
      aria-label={cyKLButtonProps['aria-label']}
    >
      {cyKLButtonProps.symbol}
    </button>
  );
};

/**
 * Pin Button Component
 * Renders the pin/unpin button for containers
 */
export const PinButton = ({ containerId, className = '' }) => {
  const { pinButtonProps, togglePin, isPinned } = useContainerSettings(containerId);
  
  if (!pinButtonProps) return null;
  
  return (
    <button
      onClick={togglePin}
      className={`
        p-1.5 rounded transition-colors
        ${isPinned 
          ? 'text-[#7ED321] bg-[#7ED321]/10' 
          : 'text-[#94A3B8] hover:text-[#E6C288] hover:bg-[#C5A059]/10'
        }
        ${className}
      `}
      title={pinButtonProps.tooltip}
      data-testid={pinButtonProps['data-testid']}
      aria-pressed={isPinned}
    >
      {isPinned ? <Pin size={16} className="fill-current" /> : <PinOff size={16} />}
    </button>
  );
};

export default useContainerSettings;
